import { useMemo, useState } from "react";
import { Plus, Coins } from "lucide-react";
import Button from "../ui/Button";
import ToolForm from "./ToolForm";
import { formatUSD } from "../../lib/formatters";
import { allocationsSummary, costTypeSummary, toolMatchesFilters } from "../../lib/allocationsLabel";

const COST_TYPE_PILL = {
  cogs: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300",
  opex: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300",
  marketing: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/20 dark:text-pink-300",
  mixed: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300",
  none: "bg-gray-50 text-gray-400 border-gray-200",
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Build the 12-month spend array for a single one-off tool by combining:
//   1. priceByMonth entries (manual mode)
//   2. matched expenses (one_off_payments rows tagged to this tool)
function computeMonths({ tool, expenses, year, fromMonth, toMonth }) {
  const months = new Array(12).fill(0);
  // Manual price entries
  const prices = tool.priceByMonth || {};
  for (const [m, v] of Object.entries(prices)) {
    const y = Number(m.slice(0, 4));
    if (y !== year) continue;
    const idx = Number(m.slice(5, 7)) - 1;
    if (idx < 0 || idx > 11) continue;
    if (idx < fromMonth - 1 || idx > toMonth - 1) continue;
    months[idx] += Number(v) || 0;
  }
  // Matched expenses for this specific tool. Refunds (direction "in")
  // subtract from the monthly spend so a $100 charge + $50 refund nets to $50.
  for (const p of expenses) {
    if (p.matchedSourceType !== "tool" || p.matchedSourceId !== tool.id) continue;
    if (!p.date) continue;
    const y = Number(p.date.slice(0, 4));
    if (y !== year) continue;
    const idx = Number(p.date.slice(5, 7)) - 1;
    if (idx < fromMonth - 1 || idx > toMonth - 1) continue;
    const sign = (p.direction || "out") === "out" ? 1 : -1;
    months[idx] += sign * (Number(p.amount) || 0);
  }
  return months;
}

export default function OneOffsView({ state, dashboard, dateFilter, deptFilter = "", costTypeFilter = "", onSelectTool }) {
  const [formOpen, setFormOpen] = useState(false);

  const tools = state.tools || [];
  const expenses = state.oneOffPayments || [];
  const offersList = state.offers || [];
  const currentYear = new Date().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonth = dateFilter?.toMonth || 12;
  const currentMonthIdx = new Date().getMonth();

  // Build a row per one-off tool with its 12-month spend.
  // Tools with no spend in the active year still appear (so newly-created
  // tools without a matched expense yet are visible as a worklist).
  const rows = useMemo(() => {
    const out = [];
    for (const t of tools) {
      if (t.type !== "one_off") continue;
      if (!toolMatchesFilters(t, deptFilter, costTypeFilter, offersList)) continue;
      const months = computeMonths({ tool: t, expenses, year, fromMonth, toMonth });
      const yearTotal = months.reduce((a, b) => a + b, 0);
      out.push({ tool: t, months, yearTotal });
    }
    out.sort((a, b) => b.yearTotal - a.yearTotal);
    return out;
  }, [tools, expenses, year, fromMonth, toMonth, deptFilter, costTypeFilter, offersList]);

  const monthlyTotals = MONTH_LABELS.map((_, i) => rows.reduce((s, r) => s + (r.months[i] || 0), 0));
  const grandTotal = monthlyTotals.reduce((a, b) => a + b, 0);

  if (rows.length === 0) {
    return (
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-8 text-center">
        <Coins size={32} className="text-brand-slate mx-auto mb-3" />
        <div className="font-medium text-brand-ink dark:text-[var(--text-primary)]">No one-off purchases for {year}</div>
        <div className="text-sm text-brand-slate mt-1 max-w-md mx-auto">
          Click "Log purchase" to add a one-time tool, credit pack, or any miscellaneous spend. You can also create one-off tools inline from the Expenses tab.
        </div>
        <div className="mt-4">
          <Button onClick={() => setFormOpen(true)}>
            <Plus size={14} /> Log purchase
          </Button>
        </div>
        <ToolForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={(data) => dashboard.addTool(data)}
          offers={state.offers || []}
          lockedType="one_off"
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
            One-off purchases · {year}
            {(dateFilter?.fromMonth || dateFilter?.toMonth) && (
              <span className="text-brand-slate font-normal text-sm ml-2">
                ({MONTH_LABELS[fromMonth - 1]}{toMonth !== fromMonth ? ` – ${MONTH_LABELS[toMonth - 1]}` : ""})
              </span>
            )}
          </div>
          <div className="text-xs text-brand-slate mt-0.5">
            {rows.length} tool{rows.length === 1 ? "" : "s"}. Click a row to see invoices assigned.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Year total</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(grandTotal)}</div>
          </div>
          <Button onClick={() => setFormOpen(true)}>
            <Plus size={14} /> Log purchase
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: "15%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "5%" }} />
            {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${63 / 12}%` }} />)}
            <col style={{ width: "6%" }} />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Tool</th>
              <th className="text-left px-2 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Assigned to</th>
              <th className="text-center px-1 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Type</th>
              {MONTH_LABELS.map((m, i) => {
                const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <th key={m} className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`}>
                    {m}
                  </th>
                );
              })}
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ tool, months, yearTotal }) => {
              const alloc = allocationsSummary(tool, state.offers || []);
              const ct = costTypeSummary(tool);
              return (
              <tr key={tool.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
                <td
                  className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue"
                  onClick={() => onSelectTool?.(tool.id)}
                  title="Open tool profile to see assigned invoices"
                >
                  {tool.name}
                </td>
                <td
                  className={`px-2 py-2 border-r border-gray-100 dark:border-[var(--border-soft)] text-[11px] truncate ${alloc.label === "—" ? "text-gray-300" : "text-brand-slate"}`}
                  title={alloc.title}
                >
                  {alloc.label}
                </td>
                <td className="px-1 py-2 text-center border-r border-gray-100 dark:border-[var(--border-soft)]" title={ct.title}>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border ${COST_TYPE_PILL[ct.kind]}`}>
                    {ct.label}
                  </span>
                </td>
                {months.map((amount, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  const color = !inRange || amount === 0
                    ? "text-gray-300"
                    : amount < 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-brand-ink dark:text-[var(--text-primary)]";
                  return (
                    <td
                      key={i}
                      className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${color}`}
                    >
                      {amount === 0 ? "—" : formatUSD(amount)}
                    </td>
                  );
                })}
                <td className={`px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)] ${yearTotal === 0 ? "text-brand-slate" : yearTotal < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                  {yearTotal === 0 ? "—" : formatUSD(yearTotal)}
                </td>
              </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50/60 dark:bg-[var(--bg-soft)]/60">
              <td colSpan={3} className="px-3 py-2 font-semibold text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                One-offs total
              </td>
              {monthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const color = amount < 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : isCurrent
                  ? "text-brand-blue-dark"
                  : "text-brand-ink dark:text-[var(--text-primary)]";
                return (
                  <td
                    key={i}
                    className={`px-1 py-2 text-center font-semibold ${isCurrent ? "bg-brand-blue-soft/30" : ""} ${color}`}
                  >
                    {amount === 0 ? "—" : formatUSD(amount)}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-bold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                {formatUSD(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <ToolForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={(data) => dashboard.addTool(data)}
        offers={state.offers || []}
        lockedType="one_off"
      />
    </div>
  );
}
